import { Grid, Divider, TextField, IconButton, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import logs from "layouts/logs";
import { PermissionType } from "models/BO/BoRole";
import { Ticket, TicketStatus } from "models/customer-supports/Ticket";
import { useEffect, useState } from "react";
import {
  addResponse,
  getTicketLogs,
  getTicketResponses,
  insertTicketLog,
  updateTicketLog,
  deleteTicketLog,
} from "utils/customerSupportUtils";
import { Message } from "./message";
import { useMaterialUIController } from "context";
import SendIcon from "@mui/icons-material/Send";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { TicketLog, TicketLogType } from "models/customer-supports/TicketLog";
import { formatDateOrTime } from "utils/globalsUtils";
import { BoUser } from "models/BO/BoUser";
import { ImageDialog } from "components/alove/ImageDialog";
import { ImageUploader } from "components/alove/ImageUploader";
import { PageNumber } from "accessNumbers";

const MDTypography = require("components/MDTypography").default;
const MDButton = require("components/MDButton").default;

// Helper to convert URLs in text to clickable links
function linkify(text: string) {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.replace(urlRegex, (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`);
}

export const ReportInnerConversation = ({ ticket, users }: { ticket: Ticket; users: BoUser[] }) => {
  const pageNumber = PageNumber.CustomerSupportTicketView;
  const [controller, dispatch] = useMaterialUIController();
  const { currentRole, currentUser } = controller;

  const [logs, setLogs] = useState([] as TicketLog[]);
  const [loader, setLoader] = useState(false);
  let [newLog, setNewLog] = useState("");
  const [editingLogId, setEditingLogId] = useState<number | null>(null);
  const [editLogText, setEditLogText] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [logToDelete, setLogToDelete] = useState<number | null>(null);

  useEffect(() => {
    if (users.length) {
      getTickeLogs();
    }
  }, [users]);

  const getTickeLogs = async () => {
    const data = await getTicketLogs(ticket.id!.toString());
    data.forEach((log) => {
      log.user = users.find((u) => u.id === log.userId);
    });
    setLogs(data);
  };

  const sendLog = async (e) => {
    if ((e?._reactName === "onClick" || e?.code === "Enter") && newLog.length > 0) {
      e.preventDefault();
      setLoader(true);
      await insertTicketLog(ticket.id!.toString(), newLog);
      setNewLog("");
      setLoader(false);
      getTickeLogs();
    }
  };

  const handleEditClick = (log: TicketLog) => {
    setEditingLogId(log.id);
    setEditLogText(log.log);
  };

  const handleEditSave = async () => {
    if (editingLogId && editLogText.length > 0) {
      await updateTicketLog(ticket.id!.toString(), editingLogId, editLogText);
      setEditingLogId(null);
      setEditLogText("");
      getTickeLogs();
    }
  };

  const handleDeleteClick = (logId: number) => {
    setLogToDelete(logId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (logToDelete) {
      await deleteTicketLog(ticket.id!.toString(), logToDelete);
      setDeleteDialogOpen(false);
      setLogToDelete(null);
      getTickeLogs();
    }
  };

  const getNames = (id: number[]) => {
    return users
      .filter((u) => id.includes(u.id))
      .map((u) => u.fullName)
      .join(", ");
  };

  const getLogContent = (log: TicketLog) => {
    switch (log.type) {
      case TicketLogType.message:
        if (editingLogId === log.id) {
          return (
            <Grid container spacing={1} alignItems="center">
              <Grid size="grow">
                <TextField
                  fullWidth
                  multiline
                  value={editLogText}
                  onChange={(e) => setEditLogText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleEditSave()}
                />
              </Grid>
              <Grid>
                <IconButton onClick={handleEditSave} color="primary">
                  <SendIcon />
                </IconButton>
              </Grid>
            </Grid>
          );
        }
        return (
          <Grid container spacing={1} alignItems="center">
            <Grid size="grow">
              <MDTypography 
                variant="body2" 
                style={{ whiteSpace: "pre-wrap" }} 
                dangerouslySetInnerHTML={{
                  __html: linkify(
                    log.log
                      .replace(/\*(.*?)\*/g, "<strong>$1</strong>") // Bold
                      .replace(/~(.*?)~/g, "<u>$1</u>") // Underline
                  )
                }}
              />
            </Grid>
            {log.userId === currentUser?.id && (
              <Grid>
                <IconButton size="small" onClick={() => handleEditClick(log)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDeleteClick(log.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
            )}
          </Grid>
        );
      case TicketLogType.statusChange:
        return (
          <MDTypography variant="body2" fontWeight="bold" color="primary">
            Status changed to: {TicketStatus[log.log]}
          </MDTypography>
        );
      case TicketLogType.assigned:
        return (
          <MDTypography variant="body2" fontWeight="bold" color="secondary">
            Assigned to: {getNames(JSON.parse(log.log))}
          </MDTypography>
        );
      case TicketLogType.image:
        return <ImageDialog url={log.log} size="100px" />;
    }
  };

  return (
    <Grid container flexDirection="row" alignItems="flex-start" gap={1}>
      <Grid
        size={{
          xs: 12,
          lg: 12
        }}>
        {logs
          .sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime())
          .map((l, i) => (
            <Grid container key={i}>
              <Grid size={2}>
                <MDTypography variant="body2" fontWeight="light">
                  {formatDateOrTime(l.created)}
                </MDTypography>
              </Grid>

              <Grid size={2}>
                <a href={`#/systemUsers/${l.userId}`}>
                  <MDTypography variant="body2" fontWeight="bold">
                    {l.user?.fullName}
                  </MDTypography>
                </a>
              </Grid>
              <Grid size={8}>
                {getLogContent(l)}
              </Grid>
            </Grid>
          ))}
        <Divider />
      </Grid>
      <Grid size={10}>
        <TextField
          id="standard-name"
          variant="outlined" 
          style={{ maxWidth: "500px", resize: "both", overflow: "auto" }}
          multiline
          fullWidth
          placeholder="Add response"
          autoFocus
          disabled={
            !currentRole.hasAccess(pageNumber, PermissionType.Edit) ||
            ticket?.status == TicketStatus.Closed
          }
          onChange={(e) => setNewLog(e.target.value)}
          value={newLog}
          onSubmit={(e) => sendLog(e)}
          InputProps={{
            sx: {
              textarea: {
                minHeight: "100px",
              },
            },
            endAdornment: (
              <IconButton color="secondary" aria-label="add an alarm">
                {loader ? <CircularProgress size={20} /> : <SendIcon onClick={sendLog} />}
              </IconButton>
            ),
          }}
        />
      </Grid>
      <Grid size={2}>
        {ticket.id && <ImageUploader
          folder="system-tickets"
          buttonOnly
          buttonTitle="Attach Image"
          onUploaded={([url]) =>
            insertTicketLog(ticket.id!.toString(), url, TicketLogType.image).then(() =>
              getTickeLogs()
            )
          }
        />}
      </Grid>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Message</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this message?
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setDeleteDialogOpen(false)}>Cancel</MDButton>
          <MDButton onClick={handleDeleteConfirm} color="error">Delete</MDButton>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};
